// ITER286 — Marketing Executive Dashboard
// Renders BOTH the Manager (single-outlet) and Admin (portfolio) views.
// Consumes /api/manager/marketing/dashboard and /api/admin/marketing/dashboard.
// Recharts for bar/line/pie/donut, @nivo/heatmap for the portfolio heatmap,
// hand-rolled inline SVG for sparklines (light dep footprint).

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import {
  ArrowUpRight, ArrowDownRight, Minus, TrendingUp,
  Users, Target, DollarSign, Activity, Eye, Zap, Info,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PALETTE = ["#0f172a", "#f59e0b", "#10b981", "#3b82f6",
                 "#8b5cf6", "#ef4444", "#06b6d4", "#f97316"];

// ITER286-final — dashboard period filter presets. Mirrors backend
// `resolve_period` so the label the user selects is exactly what the
// backend aggregates over.
const PERIOD_PRESETS = [
  { id: "current",  label: "Current Month" },
  { id: "previous", label: "Previous Month" },
  { id: "mtd",      label: "MTD" },
  { id: "ytd",      label: "YTD" },
  { id: "last3",    label: "Last 3 Months" },
  { id: "last6",    label: "Last 6 Months" },
  { id: "last12",   label: "Last 12 Months" },
  { id: "custom",   label: "Custom (MM/YYYY)" },
];

// ── Helpers ────────────────────────────────────────────────────
const fmtInt = (n) => (n === null || n === undefined) ? "—"
  : Number(n).toLocaleString("en-IN");
const fmtCur = (n) => (n === null || n === undefined) ? "—"
  : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtPct = (n, digits = 1) => (n === null || n === undefined) ? "—"
  : `${Number(n).toFixed(digits)}%`;
const fmtMonthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
};
const statusToColor = {
  green:   "text-emerald-600",
  amber:   "text-amber-600",
  red:     "text-rose-600",
  neutral: "text-slate-500",
};
const statusToBg = {
  green:   "bg-emerald-50 border-emerald-200",
  amber:   "bg-amber-50 border-amber-200",
  red:     "bg-rose-50 border-rose-200",
  neutral: "bg-slate-50 border-slate-200",
};

// ── Reusable KPI card ─────────────────────────────────────────
function KpiCard({ label, value, delta, deltaLabel, status = "neutral",
                    icon: Icon, sparkline, tooltip, testid }) {
  const arrow = delta === null || delta === undefined ? null
    : delta > 0 ? <ArrowUpRight className="w-4 h-4" />
    : delta < 0 ? <ArrowDownRight className="w-4 h-4" />
    : <Minus className="w-4 h-4" />;
  return (
    <div data-testid={testid}
      className={`rounded-lg border p-4 ${statusToBg[status]} relative`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {(delta !== null && delta !== undefined) && (
        <div className={`flex items-center gap-1 text-xs mt-1 ${statusToColor[status]}`}>
          {arrow}
          <span>{typeof delta === "number" ? fmtPct(Math.abs(delta)) : delta}</span>
          {deltaLabel && <span className="text-slate-500 ml-1">{deltaLabel}</span>}
        </div>
      )}
      {sparkline && sparkline.length > 1 && (
        <Sparkline data={sparkline} className="mt-2" />
      )}
      {tooltip && (
        <div className="absolute top-3 right-3 group">
          <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
          <div className="hidden group-hover:block absolute right-0 top-5 z-30
                          bg-slate-900 text-white text-xs p-2 rounded w-56 shadow-lg">
            {tooltip}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline SVG sparkline ──────────────────────────────────────
function Sparkline({ data, className = "", width = 120, height = 28 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  const trending = data[data.length - 1] >= data[0] ? "#10b981" : "#ef4444";
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={className} width="100%" height={height}>
      <polyline points={points} fill="none" stroke={trending} strokeWidth="1.5" />
    </svg>
  );
}

// ── Manager view ──────────────────────────────────────────────
function ManagerView({ data }) {
  const trend = data.trend || [];
  const cur = data.current || {};
  const roi = data.roi || {};

  const spendVsBudget = useMemo(() => trend.map(m => ({
    month: fmtMonthLabel(m.year_month),
    spend: m.total_spend || 0,
    budget: (data.budget_month || 0),  // per-month budget; snapshot
  })), [trend, data.budget_month]);

  const followersTrend = useMemo(() => trend.map(m => ({
    month: fmtMonthLabel(m.year_month),
    followers: m.closing_followers || 0,
    growth: m.follower_growth || 0,
  })), [trend]);

  const platformPie = useMemo(() =>
    Object.entries(data.spend_split?.platform || {})
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0), [data.spend_split]);
  const categoryPie = useMemo(() =>
    Object.entries(data.spend_split?.category || {})
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name: name.replace("_", " "), value })),
    [data.spend_split]);

  const contentBreakdown = [
    { type: "Posts",   count: cur.posts || 0 },
    { type: "Reels",   count: cur.reels || 0 },
    { type: "Stories", count: cur.stories || 0 },
    { type: "Videos",  count: cur.videos || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="marketing-manager-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard testid="kpi-followers"
          label="Total Followers"       value={fmtInt(cur.closing_followers)}
          delta={data.mom_growth_pct}   deltaLabel="MoM"
          status={data.status?.growth}  icon={Users}
          sparkline={trend.map(m => m.closing_followers)} />
        <KpiCard testid="kpi-growth"
          label="Followers Added"       value={fmtInt(cur.follower_growth)}
          delta={cur.growth_pct}        deltaLabel="growth"
          status={data.status?.growth}  icon={TrendingUp} />
        <KpiCard testid="kpi-reach"
          label="Reach"                 value={fmtInt(cur.reach)}
          icon={Eye} />
        <KpiCard testid="kpi-impressions"
          label="Impressions"           value={fmtInt(cur.impressions)}
          icon={Zap} />
        <KpiCard testid="kpi-engagement-rate"
          label="Engagement Rate"       value={fmtPct(cur.engagement_rate, 2)}
          status={data.status?.er}      icon={Activity} />
        <KpiCard testid="kpi-spend"
          label="Marketing Spend"       value={fmtCur(cur.total_spend)}
          icon={DollarSign} />
        <KpiCard testid="kpi-budget"
          label="Budget"                value={fmtCur(data.budget_month)}
          delta={data.budget_util_pct}
          deltaLabel="utilised"
          status={data.status?.util}    icon={Target} />
        <KpiCard testid="kpi-cost-per-follower"
          label="Cost / Follower"       value={fmtCur(cur.cost_per_follower)}
          tooltip="Total spend divided by net follower growth. Blank when growth is zero." />
        <KpiCard testid="kpi-cost-per-engagement"
          label="Cost / Engagement"     value={fmtCur(cur.cost_per_engagement)}
          tooltip="Total spend divided by total engagement (likes+comments+shares+saves)." />
        {/* ROI headline */}
        <KpiCard testid="kpi-mer"
          label="MER (₹ sales / ₹ spent)"
          value={roi.mer !== null && roi.mer !== undefined ? `${roi.mer}x` : "—"}
          tooltip={roi.caveat || "Requires finalized P&L for the month."} />
        <KpiCard testid="kpi-incremental-roi"
          label="Incremental ROI"
          value={roi.incremental_roi !== null && roi.incremental_roi !== undefined
                  ? `${roi.incremental_roi}x` : "—"}
          tooltip={`Baseline: median of trailing 3 finalized months. `
                    + (roi.baseline_period_count < 3
                        ? `Cold-start (only ${roi.baseline_period_count || 0} months baseline).`
                        : "Correlation, not causal.")} />
      </div>

      {/* Charts row 1 — Spend vs Budget + Followers trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Spend vs Budget — trailing 12 months"
          testid="chart-spend-vs-budget">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={spendVsBudget}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtCur(v)} />
              <Legend />
              <Bar dataKey="spend"  fill="#0f172a" name="Spend" />
              <Bar dataKey="budget" fill="#f59e0b" name="Budget" opacity={0.5} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Followers growth trend"
          testid="chart-followers-trend">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={followersTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtInt(v)} />
              <Line type="monotone" dataKey="followers" stroke="#0f172a" strokeWidth={2}
                     dot={{ r: 3 }} name="Total followers" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 — Platform pie + Category pie + Content breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Platform-wise spend"
          testid="chart-platform-split">
          {platformPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={platformPie} dataKey="value" nameKey="name"
                      innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {platformPie.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtCur(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart hint="No platform-tagged spend yet — tag your expense line items to enable this view." />}
        </ChartCard>

        <ChartCard title="Category-wise spend"
          testid="chart-category-split">
          {categoryPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={categoryPie} dataKey="value" nameKey="name"
                      innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {categoryPie.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtCur(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart hint="No spend in the current month." />}
        </ChartCard>

        <ChartCard title="Content mix"
          testid="chart-content-mix">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={contentBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="type" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top / worst month */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MonthCallout title="Top performing month"
          month={data.top_month} accent="emerald"
          testid="callout-top-month" />
        <MonthCallout title="Worst performing month"
          month={data.worst_month} accent="rose"
          testid="callout-worst-month" />
      </div>

      {/* ROI caveat */}
      {roi.caveat && (
        <div className="text-xs text-slate-500 italic border-l-2 border-slate-300 pl-3"
              data-testid="roi-caveat">
          {roi.caveat}
        </div>
      )}
    </div>
  );
}

function MonthCallout({ title, month, accent, testid }) {
  if (!month) {
    return (
      <div className={`rounded-lg border border-${accent}-200 bg-${accent}-50 p-4 opacity-60`}
            data-testid={testid}>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
          {title}
        </div>
        <div className="text-sm text-slate-500">No engagement data yet.</div>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border border-${accent}-200 bg-${accent}-50 p-4`}
          data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
        {title}
      </div>
      <div className="text-xl font-semibold text-slate-900 mb-1">
        {fmtMonthLabel(month.year_month)}
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs text-slate-700 mt-2">
        <div><span className="text-slate-500">Engagement</span>
              <div className="font-semibold">{fmtInt(month.engagement)}</div></div>
        <div><span className="text-slate-500">Spend</span>
              <div className="font-semibold">{fmtCur(month.total_spend)}</div></div>
        <div><span className="text-slate-500">Growth</span>
              <div className="font-semibold">{fmtInt(month.follower_growth)}</div></div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div data-testid={testid}
      className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-800 mb-3">{title}</div>
      {children}
    </div>
  );
}

function EmptyChart({ hint }) {
  return (
    <div className="h-[240px] flex items-center justify-center text-xs text-slate-400 italic px-6 text-center">
      {hint}
    </div>
  );
}

// ── Admin view ────────────────────────────────────────────────
function AdminView({ data }) {
  const kpis = data.portfolio_kpis || {};
  const rows = data.per_outlet || [];
  const [sortBy, setSortBy] = useState("followers");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortBy] ?? -Infinity;
      const bv = b[sortBy] ?? -Infinity;
      if (typeof av === "string" || typeof bv === "string")
        return String(av).localeCompare(String(bv)) * dir;
      return (av - bv) * dir;
    });
  }, [rows, sortBy, sortDir]);

  const heatmapData = useMemo(() => {
    return (data.heatmap || []).map(r => ({
      id: r.outlet_name || r.outlet_id,
      data: (r.cells || []).map(c => ({
        x: fmtMonthLabel(c.ym),
        y: c.er === null ? 0 : Number(c.er.toFixed(2)),
      })),
    }));
  }, [data.heatmap]);

  const portfolioTrend = useMemo(() =>
    (data.portfolio_trend || []).map(m => ({
      month: fmtMonthLabel(m.year_month),
      followers: m.followers,
      spend: m.spend,
      engagement: m.engagement,
    })), [data.portfolio_trend]);

  const clickSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };
  const sortIcon = (col) => sortBy !== col ? "" :
                              sortDir === "desc" ? " ▼" : " ▲";

  return (
    <div className="space-y-6" data-testid="marketing-admin-dashboard">
      {/* Executive KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard testid="admin-kpi-followers"
          label="Portfolio Followers" value={fmtInt(kpis.followers)}
          delta={kpis.growth_pct}      deltaLabel="MoM"
          icon={Users} />
        <KpiCard testid="admin-kpi-growth"
          label="Growth"               value={fmtPct(kpis.growth_pct)}
          icon={TrendingUp} />
        <KpiCard testid="admin-kpi-spend"
          label="Portfolio Spend"      value={fmtCur(kpis.spend)}
          icon={DollarSign} />
        <KpiCard testid="admin-kpi-budget-util"
          label="Budget Utilisation"   value={fmtPct(kpis.budget_util_pct)}
          icon={Target} />
        <KpiCard testid="admin-kpi-reach"
          label="Total Reach"          value={fmtInt(kpis.reach)}
          icon={Eye} />
        <KpiCard testid="admin-kpi-engagement"
          label="Total Engagement"     value={fmtInt(kpis.engagement)}
          icon={Activity} />
      </div>

      {/* Comparison table */}
      <ChartCard title="Outlet comparison" testid="admin-comparison-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="outlet-comparison-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                {[
                  ["outlet_name",   "Outlet"],
                  ["followers",     "Followers"],
                  ["growth_pct",    "Growth"],
                  ["spend",         "Spend"],
                  ["budget",        "Budget"],
                  ["budget_util_pct","Util"],
                  ["engagement_rate","ER"],
                  ["reach",         "Reach"],
                  ["impressions",   "Impressions"],
                  ["best_platform", "Top Platform"],
                ].map(([k, l]) => (
                  <th key={k}
                    className="py-2 px-2 cursor-pointer hover:text-slate-800"
                    onClick={() => clickSort(k)}
                    data-testid={`sort-${k}`}>
                    {l}{sortIcon(k)}
                  </th>
                ))}
                <th className="py-2 px-2">Trend</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-slate-400 italic">
                  No outlets in scope.
                </td></tr>
              )}
              {sorted.map(o => (
                <tr key={o.outlet_id}
                    className="border-b hover:bg-slate-50"
                    data-testid={`comparison-row-${o.outlet_id}`}>
                  <td className="py-2 px-2 font-medium">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      o.status === "green" ? "bg-emerald-500" :
                      o.status === "amber" ? "bg-amber-500" :
                      o.status === "red"   ? "bg-rose-500"   : "bg-slate-300"
                    }`} />
                    {o.outlet_name}
                  </td>
                  <td className="py-2 px-2">{fmtInt(o.followers)}</td>
                  <td className={`py-2 px-2 ${statusToColor[o.status]}`}>
                    {fmtPct(o.growth_pct)}
                  </td>
                  <td className="py-2 px-2">{fmtCur(o.spend)}</td>
                  <td className="py-2 px-2">{fmtCur(o.budget)}</td>
                  <td className="py-2 px-2">{fmtPct(o.budget_util_pct)}</td>
                  <td className="py-2 px-2">{fmtPct(o.engagement_rate, 2)}</td>
                  <td className="py-2 px-2">{fmtInt(o.reach)}</td>
                  <td className="py-2 px-2">{fmtInt(o.impressions)}</td>
                  <td className="py-2 px-2 text-xs text-slate-600">
                    {o.best_platform || "—"}
                  </td>
                  <td className="py-2 px-2">
                    <Sparkline data={o.sparkline || []} width={90} height={22} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Followers growth — portfolio" testid="admin-chart-followers">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={portfolioTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => fmtInt(v)} />
              <Line type="monotone" dataKey="followers" stroke="#0f172a"
                     strokeWidth={2} dot={{ r: 3 }} name="Followers" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Spend vs Engagement" testid="admin-chart-spend-eng">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={portfolioTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis yAxisId="left" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left"  dataKey="spend"      fill="#0f172a" name="Spend ₹" />
              <Bar yAxisId="right" dataKey="engagement" fill="#f59e0b" name="Engagement" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Portfolio Heatmap */}
      <ChartCard title="Engagement rate heatmap — outlets × months"
        testid="admin-heatmap">
        <div style={{ height: Math.max(240, 40 * heatmapData.length + 60) }}>
          {heatmapData.length > 0 ? (
            <ResponsiveHeatMap
              data={heatmapData}
              margin={{ top: 30, right: 60, bottom: 40, left: 120 }}
              valueFormat=">-.2f"
              colors={{ type: "sequential", scheme: "greens" }}
              emptyColor="#f1f5f9"
              axisTop={{ tickRotation: -45 }}
              axisLeft={{ tickPadding: 6 }}
              labelTextColor="#0f172a"
              theme={{ fontSize: 10, tooltip: { container: { fontSize: 12 }} }}
              hoverTarget="cell"
            />
          ) : (
            <EmptyChart hint="No outlets yet." />
          )}
        </div>
      </ChartCard>
    </div>
  );
}

// ── Period filter bar ────────────────────────────────────────
function PeriodFilterBar({ period, setPeriod, year, month, setYm }) {
  const showCustom = period === "custom";
  const now = new Date();
  const years = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) years.push(y);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2"
          data-testid="period-filter-bar">
      <span className="text-xs uppercase tracking-wider text-slate-500 mr-2 font-medium">
        Period
      </span>
      {PERIOD_PRESETS.map(p => (
        <button key={p.id} type="button"
          onClick={() => setPeriod(p.id)}
          data-testid={`period-btn-${p.id}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${
            period === p.id
              ? "bg-slate-900 text-amber-200 border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
          }`}>
          {p.label}
        </button>
      ))}
      {showCustom && (
        <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-200"
              data-testid="period-custom-picker">
          <select value={month}
            onChange={(e) => setYm(year, Number(e.target.value))}
            data-testid="period-custom-month"
            className="text-xs border border-slate-300 rounded px-2 py-1.5">
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(2000, i, 1).toLocaleString("en-IN", { month: "short" })}
              </option>
            ))}
          </select>
          <select value={year}
            onChange={(e) => setYm(Number(e.target.value), month)}
            data-testid="period-custom-year"
            className="text-xs border border-slate-300 rounded px-2 py-1.5">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

// ── Top-level component ───────────────────────────────────────
const PERIOD_STORAGE_KEY = "mktDashboardPeriod";

export default function MarketingDashboard({ mode = "manager", portfolioId = "", outletId = "" }) {
  const now = new Date();
  const persisted = (() => {
    try {
      const raw = localStorage.getItem(PERIOD_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [period, setPeriod] = useState(persisted?.period || "current");
  const [year,   setYear]   = useState(persisted?.year   || now.getFullYear());
  const [month,  setMonth]  = useState(persisted?.month  || (now.getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Persist selection across module navigations.
  useEffect(() => {
    try {
      localStorage.setItem(PERIOD_STORAGE_KEY,
        JSON.stringify({ period, year, month }));
    } catch { /* storage unavailable */ }
  }, [period, year, month]);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setErr(null);
      try {
        const base = mode === "admin"
          ? `${API}/api/admin/marketing/dashboard`
          : `${API}/api/manager/marketing/dashboard`;
        const params = new URLSearchParams({ period, year, month });
        // ITER-marketing-pass1 — Admin scope wiring. Parent AdminMarketing.js
        // owns the single Portfolio/Outlet selector; forward that scope to
        // the executive dashboard endpoint. Only send outlet_ids when a
        // specific outlet is selected.
        if (mode === "admin") {
          if (portfolioId) params.append("portfolio_id", portfolioId);
          if (outletId)    params.append("outlet_ids",   outletId);
        }
        const token = localStorage.getItem("token");
        const res = await axios.get(`${base}?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setData(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message || "Load failed");
      } finally { setLoading(false); }
    };
    load();
  }, [mode, period, year, month, portfolioId, outletId]);

  const setYm = (y, m) => { setYear(y); setMonth(m); };

  // ITER286-fix — empty-state detector. Backend returns a shaped payload
  // (e.g. a trailing 12-month array of zero-filled months) even when there
  // is no activity. We treat "no meaningful marketing activity in scope"
  // as empty by inspecting the aggregated metrics themselves.
  const isEmpty = useMemo(() => {
    if (!data) return false;
    if (mode === "admin") {
      const kpis = data.portfolio_kpis || {};
      const rows = data.per_outlet || [];
      const hasKpi = (kpis.followers || 0) > 0 || (kpis.spend || 0) > 0
                    || (kpis.reach || 0) > 0 || (kpis.engagement || 0) > 0
                    || (kpis.impressions || 0) > 0;
      const hasOutletData = rows.some(o =>
        (o.followers || 0) > 0 || (o.spend || 0) > 0
        || (o.reach || 0) > 0 || (o.engagement_rate || 0) > 0
      );
      return !hasKpi && !hasOutletData;
    }
    const cur = data.current || {};
    const trend = data.trend || [];
    const hasCur = (cur.closing_followers || 0) > 0 || (cur.total_spend || 0) > 0
                  || (cur.reach || 0) > 0 || (cur.impressions || 0) > 0
                  || (cur.follower_growth || 0) > 0
                  || (cur.engagement_rate || 0) > 0;
    const hasTrendData = trend.some(m =>
      (m.closing_followers || 0) > 0 || (m.total_spend || 0) > 0
      || (m.follower_growth || 0) > 0 || (m.reach || 0) > 0
    );
    return !hasCur && !hasTrendData;
  }, [data, mode]);

  return (
    <div className="space-y-4">
      <PeriodFilterBar period={period} setPeriod={setPeriod}
                        year={year} month={month} setYm={setYm} />
      {!loading && !err && data?.period?.label && (
        <div className="text-xs text-slate-500" data-testid="period-scope-label">
          Showing: <span className="font-medium text-slate-700">{data.period.label}</span>
        </div>
      )}
      {loading ? (
        <div className="py-3 text-xs text-slate-500 flex items-center gap-2"
              data-testid="dashboard-loading">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          Loading dashboard…
        </div>
      ) : err ? (
        <div className="p-6 rounded border border-rose-200 bg-rose-50 text-rose-800 text-sm"
              data-testid="dashboard-error">
          {err}
        </div>
      ) : isEmpty ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center"
              data-testid="dashboard-empty">
          <div className="text-sm text-slate-600">
            No marketing data available for the selected period.
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Try a different period or add entries from the Marketing module.
          </div>
        </div>
      ) : data ? (
        mode === "admin" ? <AdminView data={data} /> : <ManagerView data={data} />
      ) : null}
    </div>
  );
}
